import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#162A40',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#22C55E', blue:'#FFFFFF',
 red:'#EF4444', redBg:'rgba(239,68,68,.10)', redBorder:'rgba(239,68,68,.25)',
 amber:'#F59E0B', amberBg:'rgba(245,158,11,.10)', amberBorder:'rgba(245,158,11,.25)',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : '—';
const today = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'});

const RESORT = {
 name: 'Botanix Nature Resort',
 company: 'Botanix Resort Pvt. Ltd.',
 tagline: 'A Homegrown Botanical Retreat Near Gurgaon',
 address: 'Village Damdama, Tehsil Sohna, Gurgaon-122102, Haryana, India',
 phone: '+91-9211445430',
 email: 'info@botanix.in',
 website: 'www.botanix.in',
 gstin: '06AAGCB9754R1ZT',
 pan: 'AAGCB9754R',
 bank: 'Botanix Resort Pvt. Ltd. | A/C: 031405004054 | IFSC: ICIC0000314 | ICICI Bank, Sushant Lok, Gurgaon',
 logo: 'https://botanix.in/wp-content/uploads/2025/12/Botanix-Nature-Resort-Logo.webp',
};

const PRINT_CSS = `
 @media print {
 body * { visibility: hidden; }
 #print-area, #print-area * { visibility: visible; }
 #print-area { position: absolute; left: 0; top: 0; width: 100%; }
 @page { margin: 15mm; }
 }
 #print-area { font-family: Arial, sans-serif; color: #1A2E1E; font-size: 12px; line-height: 1.5; }
`;

// Parse booking notes
const parseNotes = (notes) => {
 const n = notes || '';
 const wMatch = n.match(/<<<WEDDING_DATA:(.*?)>>>/s);
 const cMatch = n.match(/<<<CORP_DATA:(.*?)>>>/s);
 const rMatch = n.match(/<<<ROOM_DATA:(.*?)>>>/s);
 const gMatch = n.match(/<<<GROUP_DATA:(.*?)>>>/s);
 if (wMatch) try { return { type:'wedding', ...JSON.parse(wMatch[1]) }; } catch {}
 if (cMatch) try { return { type:'corporate', ...JSON.parse(cMatch[1]) }; } catch {}
 if (rMatch) try { return { type:'room', ...JSON.parse(rMatch[1]) }; } catch {}
 if (gMatch) try { return { type:'group', ...JSON.parse(gMatch[1]) }; } catch {}
 return null;
};

// ── DOCUMENT HEADER ───────────────────────────────────────────────────────────
const DocHeader = ({ title, subtitle, booking }) => (
 <div style={{ borderBottom:'3px solid #3AAF58', paddingBottom:16, marginBottom:20 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
 <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
 <img src={RESORT.logo} alt="Botanix" style={{ height:65, width:'auto', objectFit:'contain' }} onError={e=>e.target.style.display='none'} />
 <div>
 <div style={{ fontSize:18, fontWeight:900, color:'#2E8B47' }}>{RESORT.company}</div>
 <div style={{ fontSize:13, fontWeight:600, color:'#3AAF58' }}>{RESORT.name}</div>
 <div style={{ fontSize:11, color:'#7A9A80', marginTop:2 }}>{RESORT.tagline}</div>
 <div style={{ fontSize:11, color:'#3D5E44', marginTop:3 }}>{RESORT.address}</div>
 <div style={{ fontSize:11, color:'#3D5E44' }}> {RESORT.phone} | {RESORT.email}</div>
 <div style={{ fontSize:11, color:'#3D5E44' }}>GSTIN: {RESORT.gstin} | PAN: {RESORT.pan}</div>
 </div>
 </div>
 <div style={{ textAlign:'right', flexShrink:0, marginLeft:16 }}>
 <div style={{ fontSize:20, fontWeight:800, color:'#3AAF58' }}>{title}</div>
 {subtitle && <div style={{ fontSize:12, color:'#3D5E44', marginTop:2 }}>{subtitle}</div>}
 {booking && <div style={{ fontSize:13, fontWeight:700, color:'#1A2E1E', marginTop:6 }}>Ref: {booking.booking_code}</div>}
 <div style={{ fontSize:11, color:'#7A9A80', marginTop:2 }}>Date: {today}</div>
 </div>
 </div>
 </div>
);

// ── GUEST INFO ────────────────────────────────────────────────────────────────
const GuestInfo = ({ booking }) => {
 const parsed = parseNotes(booking.notes);
 const nights = booking.start_date && booking.end_date
 ? Math.max(1, Math.ceil((new Date(booking.end_date)-new Date(booking.start_date))/86400000)) : 1;
 return (
 <div style={{ marginBottom:18 }}>
 <div style={{ background:'#F5F8F5', border:'1px solid #E2E8E2', borderRadius:8, padding:14 }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <tbody>
 {[
 ['Guest / Company Name', booking.guest_name||booking.company_name||'—'],
 ['Phone', booking.guest_phone||'—'],
 ['Email', booking.guest_email||'—'],
 ['Booking Type', booking.booking_type||'—'],
 ['Check-in Date', fmtDate(booking.start_date)],
 ['Check-out Date', fmtDate(booking.end_date)],
 ['Duration', `${nights} Night${nights>1?'s':''}`],
 ['Total Guests', `${booking.pax_count} Pax`],
 ['Nationality', parsed?.nationality||'Indian'],
 ['Status', booking.booking_status||'—'],
 ['Salesperson', booking.salesperson||'—'],
 parsed?.company ? ['Company', parsed.company] : null,
 parsed?.gstNum ? ['GST Number', parsed.gstNum] : null,
 ].filter(Boolean).map(([k,v])=>(
 <tr key={k} style={{ borderBottom:'1px solid #E2E8E2' }}>
 <td style={{ padding:'6px 10px', fontWeight:600, color:'#3D5E44', width:'35%', fontSize:12 }}>{k}</td>
 <td style={{ padding:'6px 10px', color:'#1A2E1E', fontSize:12 }}>{v}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 );
};

// ── ROOM DETAILS SECTION ──────────────────────────────────────────────────────
const RoomDetails = ({ parsed, booking }) => {
 if (!parsed || parsed.type !== 'room') return null;
 const nights = booking.start_date && booking.end_date
 ? Math.max(1, Math.ceil((new Date(booking.end_date)-new Date(booking.start_date))/86400000)) : 1;
 const allocs = parsed.occupancy || parsed.rooms || [];
 if (!allocs.length) return null;

 const MEAL_LABELS = { EP:'Room Only', CP:'+ Breakfast', MAP:'+ Breakfast & Dinner', AP:'+ All Meals' };
 const OCC_LABELS = { single:'Single Occupancy', double:'Double Sharing', triple:'Triple Sharing' };

 return (
 <div style={{ marginBottom:18 }}>
 <div style={{ fontSize:14, fontWeight:700, color:'#1A2E1E', marginBottom:10, borderBottom:'2px solid #E2E8E2', paddingBottom:6 }}>
 Room Allocation Details
 </div>
 <table style={{ width:'100%', borderCollapse:'collapse', border:'1px solid #E2E8E2' }}>
 <thead>
 <tr style={{ background:'#2E8B47', color:'#fff' }}>
 {['#','Room Type','Occupancy','Rooms','Meal Plan','Rate/Night','Nights','Amount'].map(h=>(
 <th key={h} style={{ padding:'7px 10px', fontSize:11, textAlign:'left' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {allocs.map((r,i)=>{
 const rate = parseFloat(r.rate||0);
 const rooms = parseInt(r.rooms||1);
 const mult = r.occupancy==='triple'?1.2:1;
 const amount = rate*mult*rooms*nights;
 return (
 <tr key={i} style={{ borderBottom:'1px solid #E2E8E2', background:i%2===0?'#fff':'#F5F8F5' }}>
 <td style={{ padding:'7px 10px', fontSize:12 }}>{i+1}</td>
 <td style={{ padding:'7px 10px', fontSize:12, fontWeight:600 }}>Room {i+1}</td>
 <td style={{ padding:'7px 10px', fontSize:12 }}>{OCC_LABELS[r.occupancy]||r.occupancy||'Double Sharing'}</td>
 <td style={{ padding:'7px 10px', fontSize:12, textAlign:'center' }}>{rooms}</td>
 <td style={{ padding:'7px 10px', fontSize:12 }}>{MEAL_LABELS[r.meal_plan]||r.meal_plan||'CP'}</td>
 <td style={{ padding:'7px 10px', fontSize:12, textAlign:'right' }}>{inr(rate)}</td>
 <td style={{ padding:'7px 10px', fontSize:12, textAlign:'center' }}>{nights}</td>
 <td style={{ padding:'7px 10px', fontSize:12, textAlign:'right', fontWeight:700 }}>{inr(amount)}</td>
 </tr>
 );
 })}
 </tbody>
 </table>

 {/* Children breakdown */}
 {(parsed.children6to10>0 || parsed.children10to18>0) && (
 <div style={{ marginTop:10, padding:10, background:'#F5F8F5', border:'1px solid #E2E8E2', borderRadius:6 }}>
 <div style={{ fontWeight:600, fontSize:12, color:'#2E8B47', marginBottom:6 }}>Children Charges:</div>
 {parsed.children10to18>0 && <div style={{ fontSize:12, color:'#1A2E1E' }}>• Children 10-18 yrs: {parsed.children10to18} × {inr(parsed.childRate10to18||3500)} = {inr(parsed.children10to18*(parsed.childRate10to18||3500))}</div>}
 {parsed.children6to10>0 && <div style={{ fontSize:12, color:'#1A2E1E' }}>• Children 6-10 yrs: {parsed.children6to10} × {inr(parsed.childRate6to10||2500)} = {inr(parsed.children6to10*(parsed.childRate6to10||2500))}</div>}
 <div style={{ fontSize:11, color:'#7A9A80', marginTop:4 }}>• Children below 6 yrs: Complimentary</div>
 </div>
 )}

 {/* Activities */}
 {parsed.activities?.length>0 && (
 <div style={{ marginTop:10, padding:10, background:'#EAF7EE', border:'1px solid #CDD8CD', borderRadius:6 }}>
 <div style={{ fontWeight:600, fontSize:12, color:'#2E8B47', marginBottom:4 }}>Activities Booked:</div>
 <div style={{ fontSize:12, color:'#1A2E1E' }}>{parsed.activities.join(' · ')}</div>
 </div>
 )}
 </div>
 );
};

// ── BOOKING CONFIRMATION ──────────────────────────────────────────────────────
const BookingConfirmation = ({ booking }) => {
 const parsed = parseNotes(booking.notes);
 const balance = parseFloat(booking.balance_amount||0);

 return (
 <div id="print-area" style={{ background:'#fff', padding:28, maxWidth:820, margin:'0 auto' }}>
 <DocHeader title="BOOKING CONFIRMATION" booking={booking} />
 <GuestInfo booking={booking} />
 <RoomDetails parsed={parsed} booking={booking} />

 {/* Wedding/Corporate food */}
 {parsed?.type==='wedding' && parsed.days?.length>0 && (
 <div style={{ marginBottom:18 }}>
 <div style={{ fontSize:14, fontWeight:700, color:'#1A2E1E', marginBottom:10, borderBottom:'2px solid #E2E8E2', paddingBottom:6 }}> Confirmed Menu</div>
 {parsed.days.map((day,idx)=>(
 <div key={idx} style={{ marginBottom:8 }}>
 <div style={{ fontWeight:700, fontSize:13, color:'#2E8B47', marginBottom:4 }}>Day {idx+1}</div>
 {Object.entries(day.meals||{}).map(([mt,md])=>(
 <div key={mt} style={{ marginBottom:4, paddingLeft:10 }}>
 <div style={{ fontWeight:600, fontSize:12, color:'#3D5E44' }}>{mt.replace(/_/g,' ').toUpperCase()} — {md.pax} pax</div>
 {md.food_items?.length>0 && <div style={{ fontSize:11, color:'#7A9A80' }}>{md.food_items.join(' · ')}</div>}
 </div>
 ))}
 </div>
 ))}
 </div>
 )}

 {/* Payment summary */}
 <div style={{ marginBottom:18 }}>
 <div style={{ fontSize:14, fontWeight:700, color:'#1A2E1E', marginBottom:10, borderBottom:'2px solid #E2E8E2', paddingBottom:6 }}> Payment Summary</div>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <tbody>
 {[
 ['Total Amount', inr(booking.total_amount), false],
 ['Advance Paid', inr(booking.advance_paid), false],
 ['Balance Due', inr(balance), balance>0],
 ].map(([k,v,highlight])=>(
 <tr key={k} style={{ borderBottom:'1px solid #E2E8E2', background:highlight&&balance>0?'#FDEAEA':'transparent' }}>
 <td style={{ padding:'8px 10px', fontWeight:highlight?700:400, color:highlight&&balance>0?'#C83333':'#3D5E44', fontSize:13 }}>{k}</td>
 <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:700, color:highlight&&balance>0?'#C83333':'#1A2E1E', fontSize:highlight?16:13 }}>{v}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 <div style={{ background:'#EAF7EE', border:'1px solid #CDD8CD', borderRadius:8, padding:14, marginBottom:16 }}>
 <div style={{ fontSize:13, fontWeight:700, color:'#2E8B47', marginBottom:6 }}> Payment Details</div>
 <div style={{ fontSize:12, color:'#3D5E44' }}>{RESORT.bank}</div>
 {balance>0 && <div style={{ fontSize:13, fontWeight:700, color:'#C83333', marginTop:6 }}> Balance of {inr(balance)} to be paid before check-in</div>}
 </div>

 <div style={{ fontSize:11, color:'#7A9A80', borderTop:'1px solid #E2E8E2', paddingTop:10 }}>
 <div style={{ fontWeight:700, marginBottom:4, color:'#3D5E44' }}>Terms & Conditions:</div>
 <div>• This confirmation is subject to availability and management approval.</div>
 <div>• Balance payment must be cleared before check-in / event date.</div>
 <div>• Cancellation charges apply as per resort policy.</div>
 <div>• GST will be charged as applicable @ 18%.</div>
 <div style={{ marginTop:6 }}>For queries: {RESORT.phone} | {RESORT.email}</div>
 </div>
 <div style={{ textAlign:'center', marginTop:16, fontSize:11, color:'#7A9A80' }}>
 Computer generated document · {RESORT.company} · GSTIN: {RESORT.gstin}
 </div>
 </div>
 );
};

// ── TAX INVOICE ───────────────────────────────────────────────────────────────
const TaxInvoice = ({ booking, invoice }) => {
 const parsed = parseNotes(booking.notes);
 const total = parseFloat(invoice?.total_amount||booking.total_amount||0);
 const taxable = parseFloat(invoice?.taxable_amount||Math.round(total/1.18));
 const cgst = Math.round((total-taxable)/2);
 const sgst = Math.round((total-taxable)/2);
 const paid = parseFloat(invoice?.amount_paid||booking.advance_paid||0);
 const balance = total-paid;

 return (
 <div id="print-area" style={{ background:'#fff', padding:28, maxWidth:820, margin:'0 auto' }}>
 <DocHeader title="TAX INVOICE" subtitle={`INV-${booking.booking_code}`} booking={booking} />
 <GuestInfo booking={booking} />
 <RoomDetails parsed={parsed} booking={booking} />

 <div style={{ marginBottom:18 }}>
 <table style={{ width:'100%', borderCollapse:'collapse', border:'1px solid #E2E8E2' }}>
 <thead>
 <tr style={{ background:'#2E8B47', color:'#fff' }}>
 {['#','Description','HSN/SAC','Qty','Rate','Amount'].map(h=>(
 <th key={h} style={{ padding:'8px 10px', textAlign:h==='Amount'||h==='Rate'?'right':'left', fontSize:12 }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 <tr style={{ borderBottom:'1px solid #E2E8E2' }}>
 <td style={{ padding:'8px 10px', fontSize:12 }}>1</td>
 <td style={{ padding:'8px 10px', fontSize:12 }}>{booking.booking_type} Services — {booking.pax_count} Pax</td>
 <td style={{ padding:'8px 10px', fontSize:12 }}>996311</td>
 <td style={{ padding:'8px 10px', fontSize:12 }}>1</td>
 <td style={{ padding:'8px 10px', textAlign:'right', fontSize:12 }}>{inr(taxable)}</td>
 <td style={{ padding:'8px 10px', textAlign:'right', fontSize:12 }}>{inr(taxable)}</td>
 </tr>
 </tbody>
 <tfoot>
 {[
 ['Taxable Amount', inr(taxable), false],
 ['CGST @ 9%', inr(cgst), false],
 ['SGST @ 9%', inr(sgst), false],
 ['Total Amount', inr(total), true ],
 ['Amount Paid', inr(paid), false],
 ['Balance Due', inr(balance), balance>0],
 ].map(([k,v,bold],i)=>(
 <tr key={i} style={{ borderTop:bold?'2px solid #3AAF58':'none', background:bold?'#EAF7EE':i===5&&balance>0?'#FDEAEA':'transparent' }}>
 <td colSpan={5} style={{ padding:'6px 10px', textAlign:'right', fontWeight:bold?700:400, fontSize:bold?14:12, color:i===5&&balance>0?'#C83333':'#1A2E1E' }}>{k}</td>
 <td style={{ padding:'6px 10px', textAlign:'right', fontWeight:bold?700:400, fontSize:bold?14:12, color:i===5&&balance>0?'#C83333':'#1A2E1E' }}>{v}</td>
 </tr>
 ))}
 </tfoot>
 </table>
 </div>

 <div style={{ background:'#EAF7EE', borderRadius:8, padding:12, marginBottom:14 }}>
 <div style={{ fontSize:12, color:'#3D5E44' }}>{RESORT.bank}</div>
 </div>
 <div style={{ fontSize:11, color:'#7A9A80', textAlign:'center' }}>
 Computer generated invoice · {RESORT.company} · GSTIN: {RESORT.gstin}
 </div>
 </div>
 );
};

// ── QUOTATION ─────────────────────────────────────────────────────────────────
const Quotation = ({ booking }) => {
 const parsed = parseNotes(booking.notes);
 return (
 <div id="print-area" style={{ background:'#fff', padding:28, maxWidth:820, margin:'0 auto' }}>
 <DocHeader title="QUOTATION / PROFORMA" subtitle={`QT-${booking.booking_code}`} booking={booking} />
 <div style={{ background:'#FEF6E7', border:'1px solid #F0D499', borderRadius:8, padding:12, marginBottom:14 }}>
 <div style={{ fontSize:12, color:'#856404', fontWeight:600 }}> Quotation only — Not a confirmed booking. Valid for 7 days.</div>
 </div>
 <GuestInfo booking={booking} />
 <RoomDetails parsed={parsed} booking={booking} />
 <div style={{ background:'#EAF7EE', border:'1px solid #CDD8CD', borderRadius:8, padding:14 }}>
 <div style={{ fontSize:13, fontWeight:700, color:'#2E8B47', marginBottom:6 }}>To Confirm This Booking:</div>
 <div style={{ fontSize:12, color:'#3D5E44' }}>• Pay minimum 25% advance to block dates</div>
 <div style={{ fontSize:12, color:'#3D5E44' }}>• {RESORT.bank}</div>
 <div style={{ fontSize:12, color:'#3D5E44' }}>• Call: {RESORT.phone}</div>
 </div>
 </div>
 );
};

// ── DAY PROGRAMME ─────────────────────────────────────────────────────────────
const DayProgramme = ({ booking }) => {
 const parsed = parseNotes(booking.notes);
 const schedule = [
 {time:'09:00 AM', activity:'Guest Arrival & Check-in'},
 {time:'09:30 AM', activity:'Welcome & Room Allocation'},
 {time:'10:00 AM', activity:'Breakfast (if applicable)'},
 {time:'12:00 PM', activity:'Activities / Leisure'},
 {time:'01:30 PM', activity:'Lunch'},
 {time:'04:00 PM', activity:'Hi-Tea'},
 {time:'07:00 PM', activity:'Evening Programme'},
 {time:'08:30 PM', activity:'Dinner'},
 ];
 return (
 <div id="print-area" style={{ background:'#fff', padding:28, maxWidth:820, margin:'0 auto' }}>
 <DocHeader title="DAY PROGRAMME" booking={booking} />
 <GuestInfo booking={booking} />
 <RoomDetails parsed={parsed} booking={booking} />
 <div style={{ marginBottom:18 }}>
 <div style={{ fontSize:14, fontWeight:700, color:'#1A2E1E', marginBottom:10, borderBottom:'2px solid #E2E8E2', paddingBottom:6 }}> Schedule</div>
 {schedule.map((s,i)=>(
 <div key={i} style={{ display:'flex', gap:16, padding:'8px 0', borderBottom:'1px solid #E2E8E2', alignItems:'center' }}>
 <div style={{ width:90, fontWeight:700, color:'#2E8B47', fontSize:13, flexShrink:0 }}>{s.time}</div>
 <div style={{ fontSize:13, color:'#1A2E1E' }}>{s.activity}</div>
 </div>
 ))}
 </div>
 <div style={{ fontSize:11, color:'#7A9A80', textAlign:'center' }}>
 {RESORT.company} · {RESORT.phone} · {RESORT.email}
 </div>
 </div>
 );
};

// ── PRINT PREVIEW MODAL ───────────────────────────────────────────────────────
function PrintPreview({ booking, docType, invoice, onClose }) {
 const handlePrint = () => {
 const style = document.createElement('style');
 style.textContent = PRINT_CSS;
 document.head.appendChild(style);
 window.print();
 setTimeout(()=>document.head.removeChild(style), 1000);
 };
 const DOC = {
 confirmation: <BookingConfirmation booking={booking} />,
 invoice: <TaxInvoice booking={booking} invoice={invoice} />,
 quotation: <Quotation booking={booking} />,
 programme: <DayProgramme booking={booking} />,
 };
 return (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.85)', zIndex:2000, display:'flex', flexDirection:'column', overflow:'hidden' }}>
 <div style={{ background:'#1A1D27', padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, borderBottom:'1px solid rgba(255,255,255,.1)' }}>
 <div style={{ fontWeight:700, color:'#F0F2FF', fontSize:14 }}>
 Preview — {booking.guest_name||booking.company_name} · {booking.booking_code}
 </div>
 <div style={{ display:'flex', gap:10 }}>
 <button onClick={handlePrint} style={{ padding:'9px 22px', background:'#3AAF58', border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700 }}> Print / Save PDF</button>
 <button onClick={onClose} style={{ padding:'9px 18px', background:'none', border:'1px solid rgba(255,255,255,.2)', borderRadius:8, color:'#A0A8C0', cursor:'pointer', fontSize:14 }}> Close</button>
 </div>
 </div>
 <div style={{ flex:1, overflowY:'auto', background:'#888', padding:20 }}>
 <div style={{ background:'#fff', maxWidth:820, margin:'0 auto', boxShadow:'0 4px 30px rgba(0,0,0,.3)', borderRadius:4 }}>
 {DOC[docType]||DOC.confirmation}
 </div>
 </div>
 </div>
 );
}

// ── MAIN PRINT MODULE ─────────────────────────────────────────────────────────
export default function PrintModule() {
 const [bookings, setBookings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState('');
 const [filterType, setFilterType] = useState('All');
 const [preview, setPreview] = useState(null);
 const [docType, setDocType] = useState('confirmation');
 const [invoiceData, setInvoiceData] = useState(null);

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const r = await fetch(`${API}/bookings`);
 const d = await r.json();
 setBookings(Array.isArray(d)?d:[]);
 } catch {}
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const DOC_TYPES = [
 { id:'confirmation', label:'Booking Confirmation', },
 { id:'invoice', label:'Tax Invoice', },
 { id:'quotation', label:'Quotation', },
 { id:'programme', label:'Day Programme', },
 ];

 const bookingTypes = ['All',...new Set(bookings.map(b=>b.booking_type).filter(Boolean))];
 let filtered = bookings;
 if (search) filtered = filtered.filter(b=>[b.booking_code,b.guest_name,b.company_name,b.booking_type].join(' ').toLowerCase().includes(search.toLowerCase()));
 if (filterType!=='All') filtered = filtered.filter(b=>b.booking_type===filterType);

 const inp2 = { padding:'10px 12px', background:'#162A40', border:'1px solid rgba(30,58,95,1)', borderRadius:8, color:'#F0F2FF', fontSize:13, outline:'none' };

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> Print & Documents</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>Confirmations · Invoices · Quotations · Day Programmes</div>
 </div>
 <button onClick={load} style={{ padding:'9px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}> Refresh</button>
 </div>

 <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
 {DOC_TYPES.map(d=>(
 <button key={d.id} onClick={()=>setDocType(d.id)}
 style={{ padding:'10px 20px', background:docType===d.id?C.accent:C.surface, border:`1px solid ${docType===d.id?C.accent:C.border2}`, borderRadius:10, color:docType===d.id?'#fff':C.text2, cursor:'pointer', fontSize:13, fontWeight:600 }}>
 {d.icon} {d.label}
 </button>
 ))}
 </div>

 <div style={{ display:'flex', gap:10, marginBottom:16 }}>
 <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search booking code, guest name..."
 style={{ ...inp2, flex:1, maxWidth:320 }} />
 <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ ...inp2, width:200, cursor:'pointer' }}>
 {bookingTypes.map(t=><option key={t} style={{background:'#0F2040',color:'#F0F2FF'}}>{t}</option>)}
 </select>
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading bookings…</div>}

 {!loading && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Code','Guest','Type','Date','Pax','Amount','Status','Print'].map(h=>(
 <th key={h} style={{ padding:'10px 14px', fontSize:11, color:C.text2, fontWeight:700, textAlign:'left', textTransform:'uppercase' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {filtered.map((b,i)=>(
 <tr key={b.id} style={{ borderBottom:`1px solid rgba(30,58,95,.5)`, background:i%2===0?'transparent':C.surface2 }}>
 <td style={{ padding:'10px 14px', fontSize:12, fontWeight:700, color:C.accent }}>{b.booking_code}</td>
 <td style={{ padding:'10px 14px', fontSize:13, color:C.text }}>{b.guest_name||b.company_name||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{b.booking_type}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{b.start_date?.slice(0,10)}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text, textAlign:'center' }}>{b.pax_count}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700, color:C.amber }}>{inr(b.total_amount)}</td>
 <td style={{ padding:'10px 14px' }}>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:10, fontWeight:700,
 background:b.booking_status==='Confirmed'?`${C.accent}20`:b.booking_status==='Tentative'?`${C.amber}20`:`${C.red}20`,
 color:b.booking_status==='Confirmed'?C.accent:b.booking_status==='Tentative'?C.amber:C.red }}>
 {b.booking_status}
 </span>
 </td>
 <td style={{ padding:'10px 14px' }}>
 <div style={{ display:'flex', gap:5 }}>
 {DOC_TYPES.map(d=>(
 <button key={d.id} onClick={()=>{setDocType(d.id);setPreview(b);}}
 title={d.label}
 style={{ padding:'5px 8px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:14 }}>
 {d.icon}
 </button>
 ))}
 </div>
 </td>
 </tr>
 ))}
 {filtered.length===0 && (
 <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:C.text2 }}>No bookings found</td></tr>
 )}
 </tbody>
 </table>
 </div>
 )}

 {preview && (
 <PrintPreview booking={preview} docType={docType} invoice={invoiceData}
 onClose={()=>{ setPreview(null); setInvoiceData(null); }} />
 )}
 </div>
 );
}
